import { Chip, darken, useTheme } from '@material-ui/core';
import { ResponsiveLine } from '@nivo/line';
import { chain } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect } from 'react';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatPercent } from '../../../../logic/otherUtilities/formatting/formatPercent';
import { formatTemplate } from '../../../../logic/otherUtilities/formatting/formatTemplate';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface MarketTrendChartProps extends ChartProps {}

export const MarketTrendChart: React.FC<MarketTrendChartProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTagGroupId: true,
          showTagId: true,
          showYears: true,
        },
      })),
    []
  );
  const years = analysisFilterState?.years ?? 3;

  // Data fetching
  const { result, query } = projectsApiService.queries.analysis.useGetMonthlyTrend({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  // Styling
  const theme = useTheme();
  const chartTheme = {
    theme: {
      fontSize: 14,
      color: theme.palette.info.main,
      labels: {
        text: {
          fontSize: 14,
          color: theme.palette.info.main,
        },
      },
    },
  };

  // Render
  if (didQueryTimeout(query)) {
    return <DataTimedOut />;
  }

  if (query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (!result?.volumeSumPerMonth.length) {
    return <NotEnoughData />;
  }

  const firstYearAverage =
    chain(result.volumeSumPerMonth)
      .filter((value, index) => index < 12)
      .sumBy((v) => v.volume)
      .value() / 12;

  const secondYearAverage =
    chain(result.volumeSumPerMonth)
      .filter((value, index) => index >= 12 && index < 24)
      .sumBy((v) => v.volume)
      .value() / 12;

  const thirdYearAverage =
    chain(result.volumeSumPerMonth)
      .filter((value, index) => index >= 24 && index < 36)
      .sumBy((v) => v.volume)
      .value() / 12;

  const trend = thirdYearAverage / (years === 3 ? firstYearAverage : secondYearAverage) - 1;
  const trendYears = years === 3 ? 3 : 2;
  const trendPositive = trend >= 0;

  // The year array is used to create thick lines (markers) in the chart
  const yearArray = chain(result.volumeSumPerMonth)
    .filter((v, index) => v.month === 1)
    .map((v) => v.year)
    .uniq()
    .orderBy((v) => v)
    .value();

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="text-center">
        <Chip
          color="default"
          label={
            <>
              {formatTemplate(internationalText.annotation.year_trend[language], [trendYears])}:{' '}
              {trendPositive ? '+' : ''}
              {formatPercent(trend)}%
            </>
          }
        />
      </div>
      <div className={'h-[500px]'}>
        <ResponsiveLine
          theme={chartTheme.theme}
          data={[
            {
              id: 'mainline',
              data: result?.volumeSumPerMonth
                .filter(
                  (v, index) =>
                    years === 3 || (years === 2 && index > 12) || (years === 1 && index > 24)
                )
                .map((vsm) => ({
                  x: moment(new Date(vsm.year, vsm.month - 1, 1)).format('yyyy-MM-DD'),
                  y: vsm.volume,
                })),
            },
          ]}
          margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            useUTC: true,
            precision: 'month',
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            format: (v: Date) => {
              if (v.getMonth() === 0) return v.getFullYear();
              return 'Q' + (v.getMonth() / 3 + 1);
            },
            tickValues: 'every 3 months',
            legendOffset: -12,
          }}
          markers={yearArray.map((year) => ({
            axis: 'x',
            value: new Date(year, 0, 1),
            lineStyle: { stroke: darken(theme.palette.grey[300], 0), strokeWidth: 3 },
            legendOrientation: 'vertical',
          }))}
          axisLeft={{
            format: '~s',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
          }}
          colors={[palette.tertiaryScale[1]]}
          pointColor={palette.tertiaryScale[1]}
          pointSize={years === 3 ? 6 : years === 2 ? 8 : 12}
          lineWidth={years === 3 ? 1.5 : years === 2 ? 2 : 3}
          animate={false}
          curve={'monotoneX'}
          isInteractive={false}
        />
      </div>
    </div>
  );
};

export default MarketTrendChart;
